import React from 'react'
import {
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  theme,
  ChakraComponent,
} from '@chakra-ui/react'

const buttonStyles = {
  primary: {
    bg: 'linear-gradient(hsl(213, 62%, 56%), hsl(213, 62%, 46%))',
    color: 'white',
    borderColor: 'blue.500',
    _hover: {
      bg: 'linear-gradient(hsl(213, 62%, 50%), hsl(213, 62%, 40%))',
    },
    _active: {
      bg: 'linear-gradient(hsl(213, 62%, 45%), hsl(213, 62%, 35%))',
    },
  },
  secondary: {
    bg: 'linear-gradient(hsl(229, 52%, 30%), hsl(229, 52%, 20%))',
    color: 'white',
    borderColor: 'blue.900',
    _hover: {
      bg: 'linear-gradient(hsl(229, 52%, 25%), hsl(229, 52%, 15%))',
    },
    _active: {
      bg: 'linear-gradient(hsl(229, 52%, 20%), hsl(229, 52%, 10%))',
    },
  },
  standard: {
    bg: 'linear-gradient(hsl(0, 0%, 96%), hsl(0, 0%, 86%))',
    color: 'gray.700',
    borderColor: 'gray.400',
    _hover: {
      bg: 'linear-gradient(hsl(0, 0%, 92%), hsl(0, 0%, 82%))',
    },
    _active: {
      bg: 'linear-gradient(hsl(0, 0%, 88%), hsl(0, 0%, 78%))',
    },
  },
  positive: {
    bg: 'linear-gradient(hsl(106, 47%, 50%), hsl(106, 47%, 40%))',
    color: 'white',
    borderColor: 'green.500',
    _hover: {
      bg: 'linear-gradient(hsl(106, 47%, 47%), hsl(106, 47%, 35%))',
    },
    _active: {
      bg: 'linear-gradient(hsl(106, 47%, 43%), hsl(106, 47%, 30%))',
    },
  },
  negative: {
    bg: 'linear-gradient(hsl(3, 99%, 63%), hsl(3, 99%, 53%))',
    color: 'white',
    borderColor: 'red.500',
    _hover: {
      bg: 'linear-gradient(hsl(3, 99%, 57%), hsl(3, 99%, 47%))',
    },
    _active: {
      bg: 'linear-gradient(hsl(3, 99%, 50%), hsl(3, 99%, 40%))',
    },
  },
  orange: {
    bg: 'linear-gradient(hsl(40, 89%, 61%), hsl(40, 89%, 51%))',
    color: 'black',
    borderColor: 'orange.300',
    _hover: {
      bg: 'linear-gradient(hsl(40, 89%, 57%), hsl(40, 89%, 47%))',
    },
    _active: {
      bg: 'linear-gradient(hsl(40, 89%, 53%), hsl(40, 89%, 43%))',
    },
  },
  white: {
    bg: 'white',
    color: 'black',
    borderColor: 'gray.400',
    _hover: {
      bg: 'gray.100',
    },
    _active: {
      bg: 'gray.200',
    },
  },
  link: {
    variant: 'link',
    border: 0,
    color: 'blue.500',
    _disabled: {
      color: 'gray.500',
      ...theme.components.Button.baseStyle._disabled,
    },
  },
}

export type ButtonVariants =
  | 'standard'
  | 'primary'
  | 'secondary'
  | 'positive'
  | 'negative'
  | 'orange'
  | 'white'
  | 'link'

interface ButtonProps extends ChakraButtonProps {
  variant?: ButtonVariants
}

// This Type allows us to pass a ref to our custom Button components.
type ButtonComponent = ChakraComponent<'button', {}>

const Button = (({ variant = 'standard', ...baseProps }: ButtonProps) => {
  return <ChakraButton {...buttonStyles[variant]} {...baseProps} />
}) as ButtonComponent

export default Button
